import { useState, useEffect } from 'react';
import './App.css';
import { fetchSurveyForm, submitFeedback } from './api/client';
import SurveyQuestion from './SurveyQuestion';
import { transformFeedbackPayload } from './utils/mappers';
const programConfig = {
  title: 'Singapore Boys Scouts Program',
  logo: '/logo192.png',
  adminNote: {
    title: null,
    body: 'On behalf of the Singapore Boys Scouts program, thank you for participating in our program. Your feedback helps us improve the experience for future participants.',
  },
  includeSelective: true,
  theme: {
    backgroundColor: '#2d1b69',
    interfaceColor: '#667eea',
    title: {
      fontFamily: 'Georgia, serif',
      fontSize: '28px',
      color: '#000099',
    },
    note: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '15px',
      color: '#495057',
    },
    question: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#1a1a2e',
    },
    answer: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '15px',
      color: '#333333',
    },
  },
};


function App() {
  const config = programConfig;
  const theme = config.theme;

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveyMetadata, setSurveyMetadata] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitNetworkError, setSubmitNetworkError] = useState(null);

  useEffect(() => {
    const loadForm = async () => {
      const params = new URLSearchParams(window.location.search);
      const qrToken = params.get('qr_token');

      const response = await fetchSurveyForm(qrToken);

      if (response.error) {
        setError(response.error);
        setLoading(false);
        return;
      }

      // Map backend schema to frontend component schema
      // Dynamic Rendering: preserve raw question_type and is_core
      const mappedQuestions = response.data.questions.map(q => ({
        id: q.id.toString(),
        type: q.question_type, // 'text' or 'rating' or unknown fallback
        label: q.question_text,
        is_core: q.is_core
      }));

      setQuestions(mappedQuestions);
      setSurveyMetadata({
        title: response.data.title,
        description: response.data.description,
        logo: response.data.logo
      });
      setLoading(false);
    };

    loadForm();
  }, []);

  const updateAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Required Fields Guard
    const missingFields = [];
    questions.forEach((q) => {
      const answer = answers[q.id];
      if (q.is_core === true && (!answer || (typeof answer === 'string' && answer.trim() === ''))) {
        missingFields.push(q.id);
      }
    });

    if (missingFields.length > 0) {
      setSubmitErrors(missingFields);
      return;
    }

    setSubmitErrors([]);
    setSubmitNetworkError(null);
    setIsSubmitting(true);

    const params = new URLSearchParams(window.location.search);
    const qrToken = params.get('qr_token');

    const payload = transformFeedbackPayload(qrToken, answers, questions);
    const response = await submitFeedback(payload);

    setIsSubmitting(false);

    if (response.error) {
      setSubmitNetworkError(response.error);
      return;
    }

    console.log('Survey submitted:', response);
    setAnswers({});
    setSubmitted(true);
  };

  if (loading) {
    return (
      <div className="survey-container" style={{ backgroundColor: theme.backgroundColor }}>
        <div className="survey-card" style={{ textAlign: 'center', padding: '60px' }}>
          <h2>Loading survey...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="survey-container" style={{ backgroundColor: theme.backgroundColor }}>
        <div className="survey-card" style={{ textAlign: 'center', padding: '60px' }}>
          <h2>Oops!</h2>
          <p style={{ marginBottom: '40px', fontSize: '18px', color: '#444' }}>
            {error.message || 'Something went wrong.'}
          </p>
          <div style={{ fontSize: '12px', color: '#999', marginTop: '40px' }}>
            Error Code: {error.code}
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="survey-container" style={{ backgroundColor: theme.backgroundColor }}>
        <div className="survey-card">
          <div className="success-card">
            <div className="success-icon" style={{ background: theme.interfaceColor }}>✓</div>
            <h2>Thank you!</h2>
            <p>Your response has been recorded.</p>
            <button
              className="btn btn-primary"
              style={{ background: theme.interfaceColor }}
              onClick={() => { setAnswers({}); setSubmitted(false); }}
            >
              Submit another response
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-container" style={{ backgroundColor: theme.backgroundColor }}>
      <div className="survey-card">
        {(surveyMetadata?.logo || config.logo) && (
          <div className="survey-logo">
            <img src={surveyMetadata?.logo || config.logo} alt="Program logo" />
          </div>
        )}

        <h1
          className="survey-title"
          style={{
            fontFamily: theme.title.fontFamily,
            fontSize: theme.title.fontSize,
            color: theme.title.color,
          }}
        >
          {surveyMetadata?.title || config.title}
        </h1>

        {config.adminNote && (
          <div className="admin-note">
            {config.adminNote.title && (
              <strong
                className="admin-note-title"
                style={{
                  fontFamily: theme.note.fontFamily,
                  fontSize: theme.note.fontSize,
                  color: theme.note.color,
                }}
              >
                {config.adminNote.title}
              </strong>
            )}
            <p style={{
              fontFamily: theme.note.fontFamily,
              fontSize: theme.note.fontSize,
              color: theme.note.color,
            }}>
              {surveyMetadata?.description || config.adminNote.body}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {questions.length === 0 ? (
            <div className="empty-state" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              <p>No questions available for this session</p>
            </div>
          ) : (
            questions.map((question, index) => (
              <div key={question.id} className="question-block">
                <label
                  className="question-label"
                  style={{
                    fontFamily: theme.question.fontFamily,
                    fontSize: theme.question.fontSize,
                    color: theme.question.color,
                  }}
                >
                  {index + 1}. {question.label} {question.is_core && <span style={{ color: 'red' }}>*</span>}
                </label>
                <SurveyQuestion
                  question={question}
                  value={answers[question.id]}
                  onChange={(val) => updateAnswer(question.id, val)}
                  theme={theme}
                />
                {submitErrors.includes(question.id) && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                    This question is required.
                  </div>
                )}
              </div>
            ))
          )}

          {submitNetworkError && (
            <div className="network-error-banner" style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '20px',
              textAlign: 'center',
              cursor: 'pointer'
            }} onClick={handleSubmit}>
              Submission Failed — Tap to Retry
              <div style={{ fontSize: '12px', marginTop: '4px' }}>{submitNetworkError.message}</div>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-submit"
            style={{ background: theme.interfaceColor, opacity: isSubmitting ? 0.7 : 1 }}
            disabled={questions.length === 0 || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>

        <div className="ssehv-partner-logo">
          <span className="partner-text">An SSEHV Partner</span>
        </div>
      </div>
    </div>
  );
}

export default App;
